// Receptionist · Call Logs — presentation config
// Maps data values to labels + visual tones. Presentation/state-only (no API).

import type { IconKind } from '../components/Icon/Icon';
import type {
  CallLogsTabId,
  CallSentiment,
  InboundIntent,
  InboundOutcome,
  KpiIconKey,
  KpiTone,
  OutboundOutcome,
  OutboundStatus,
  PillTone,
  RecallTypeSlug,
} from './types';

/**
 * Belgravia green/brass palette used by the call-logs surface — mirrors the
 * Reports module's RPT_TOKENS. Everything else uses the global CSS variables.
 */
export const CL_TOKENS = {
  green: '#0F5C45',
  greenDeep: '#0A4530',
  greenSoft: 'rgba(15, 92, 69, 0.10)',
  greenWash: 'rgba(15, 92, 69, 0.05)',
  brass: '#A8845C',
  brassDeep: '#8B6B47',
  brassSoft: 'rgba(168, 132, 92, 0.10)',
  brassTint: '#F0E9DC',
  brassBorder: 'rgba(168, 132, 92, 0.18)',
  negative: '#A8442E',
  warning: '#B5803D',
  ink1: '#1C1A15',
  ink2: '#3D3A33',
  ink3: '#75706A',
  ink4: '#A8A39B',
  ink5: '#D9D5CC',
  surfaceMute: '#F2F0EA',
  surfaceSubtle: '#FBFAF6',
  border: 'rgba(28, 24, 18, 0.09)',
  borderMed: 'rgba(28, 24, 18, 0.15)',
  iriLinear:
    'linear-gradient(120deg, #4D2855 0%, #8E3567 28%, #D8625F 55%, #D49545 80%, #0F5C45 100%)',
  iriDot: 'linear-gradient(135deg, #4D2855 0%, #D8625F 55%, #D49545 100%)',
  shadowXs: '0 1px 1px rgba(28,24,18,0.04)',
} as const;

/** Pill tone → background + text colour. */
export const PILL_TONE: Record<PillTone, { bg: string; color: string }> = {
  positive: { bg: 'rgba(15, 92, 69, 0.10)', color: '#0F5C45' },
  negative: { bg: 'rgba(168, 68, 46, 0.10)', color: '#A8442E' },
  warning: { bg: 'rgba(181, 128, 61, 0.12)', color: '#B5803D' },
  neutral: { bg: '#F2F0EA', color: '#3D3A33' },
  brass: { bg: '#F0E9DC', color: '#8B6B47' },
};

export interface PillMeta {
  tone: PillTone;
  label: string;
}

/* ---- Inbound config maps ------------------------------------------------ */

export const INTENT_META: Record<InboundIntent, PillMeta> = {
  new_patient_exam: { tone: 'positive', label: 'New patient exam' },
  scheduling: { tone: 'neutral', label: 'Scheduling' },
  reschedule: { tone: 'neutral', label: 'Reschedule' },
  cancellation: { tone: 'warning', label: 'Cancellation' },
  emergency_pain: { tone: 'negative', label: 'Emergency / pain' },
  post_op_concern: { tone: 'negative', label: 'Post-op concern' },
  billing: { tone: 'warning', label: 'Billing' },
  insurance: { tone: 'warning', label: 'Insurance' },
  pricing_inquiry: { tone: 'warning', label: 'Pricing inquiry' },
  cosmetic_consult: { tone: 'positive', label: 'Cosmetic consult' },
  hours_location: { tone: 'neutral', label: 'Hours & location' },
  other: { tone: 'neutral', label: 'Other' },
};

export const OUTCOME_META: Record<InboundOutcome, PillMeta> = {
  scheduled: { tone: 'positive', label: 'Scheduled' },
  rescheduled: { tone: 'positive', label: 'Rescheduled' },
  registered: { tone: 'positive', label: 'Registered' },
  answered: { tone: 'neutral', label: 'Answered' },
  needs_callback: { tone: 'warning', label: 'Needs callback' },
  transferred: { tone: 'warning', label: 'Transferred' },
  cancelled: { tone: 'negative', label: 'Cancelled' },
  voicemail: { tone: 'neutral', label: 'Voicemail' },
  dropped: { tone: 'negative', label: 'Dropped' },
};

/** Sentiment → coloured dot + label. */
export const SENTIMENT_META: Record<CallSentiment, { color: string; label: string }> = {
  positive: { color: '#0F5C45', label: 'Positive' },
  neutral: { color: '#A8A39B', label: 'Neutral' },
  anxious: { color: '#B5803D', label: 'Anxious' },
  frustrated: { color: '#B5803D', label: 'Frustrated' },
  negative: { color: '#A8442E', label: 'Negative' },
};

/* ---- Outbound (patient / recall) config maps ---------------------------- */

export const RECALL_TYPE_META: Record<RecallTypeSlug, PillMeta> = {
  appointment_confirmation: { tone: 'neutral', label: 'Appt confirmation' },
  appointment_reminder: { tone: 'neutral', label: 'Appt reminder' },
  post_op_followup: { tone: 'positive', label: 'Post-op follow-up' },
  hygiene_recall: { tone: 'positive', label: 'Hygiene recall' },
  treatment_plan_followup: { tone: 'warning', label: 'Treatment plan' },
  balance_followup: { tone: 'warning', label: 'Balance follow-up' },
  reactivation: { tone: 'brass', label: 'Reactivation' },
};

export const OUTBOUND_OUTCOME_META: Record<OutboundOutcome, PillMeta> = {
  post_op_check_completed: { tone: 'positive', label: 'Post-op check completed' },
  post_op_concern_escalated: { tone: 'negative', label: 'Post-op concern — escalated' },
  appointment_confirmed: { tone: 'positive', label: 'Appointment confirmed' },
  appointment_booked: { tone: 'positive', label: 'Appointment booked' },
  appointment_rescheduled: { tone: 'positive', label: 'Appointment rescheduled' },
  appointment_cancelled_reschedule_pending: { tone: 'warning', label: 'Cancelled — reschedule pending' },
  balance_payment_commitment: { tone: 'positive', label: 'Payment commitment' },
  balance_payment_completed: { tone: 'positive', label: 'Payment completed' },
  balance_discussion_deferred: { tone: 'warning', label: 'Balance deferred' },
  treatment_plan_accepted: { tone: 'positive', label: 'Treatment plan accepted' },
  treatment_plan_declined: { tone: 'negative', label: 'Treatment plan declined' },
  patient_reactivated_appointment_booked: { tone: 'positive', label: 'Reactivated — booked' },
  patient_not_interested: { tone: 'negative', label: 'Not interested' },
  voicemail_left: { tone: 'neutral', label: 'Voicemail left' },
  no_contact: { tone: 'neutral', label: 'No contact' },
};

export const STATUS_META: Record<OutboundStatus, PillMeta> = {
  completed: { tone: 'positive', label: 'Completed' },
  voicemail: { tone: 'neutral', label: 'Voicemail' },
  no_answer: { tone: 'neutral', label: 'No answer' },
  failed: { tone: 'negative', label: 'Failed' },
  cancelled: { tone: 'negative', label: 'Cancelled' },
};

/* ---- Tab definitions ---------------------------------------------------- */

export interface CallLogsTabDef {
  id: CallLogsTabId;
  label: string;
}

export const CALL_LOGS_TABS: CallLogsTabDef[] = [
  { id: 'inbound', label: 'Inbound' },
  { id: 'outboundPatient', label: 'Outbound — patients' },
];

/* ---- Filter-bar chips per tab ------------------------------------------- */

export const FILTER_CHIPS_BY_TAB: Record<CallLogsTabId, string[]> = {
  inbound: ['Intent', 'Outcome', 'Sentiment', 'Escalation reason'],
  outboundPatient: ['Recall type', 'Outcome', 'Attempts'],
};

/* ---- Table columns per tab ---------------------------------------------- */

export interface CallLogsColumnDef {
  template: string;
  labels: string[];
}

export const CALL_LOGS_COLUMNS: Record<CallLogsTabId, CallLogsColumnDef> = {
  inbound: {
    template:
      'minmax(180px, 1.5fr) minmax(120px, auto) minmax(110px, auto) minmax(100px, auto) 56px 95px 28px',
    labels: ['Patient', 'Intent', 'Outcome', 'Sentiment', 'Length', 'Time', ''],
  },
  outboundPatient: {
    template:
      'minmax(180px, 1.5fr) minmax(110px, auto) minmax(70px, auto) minmax(110px, auto) 56px 95px 28px',
    labels: ['Patient', 'Recall type', 'Attempts', 'Outcome', 'Length', 'Time', ''],
  },
};

/* ---- KPI icon map → design-system IconKind ------------------------------ */
// Substitutions (no exact kind exists): 'voicemail' → 'chat'.

export const KPI_ICON_KIND: Record<KpiIconKey, IconKind> = {
  'phone-in': 'phone-in',
  'phone-out': 'phone-out',
  'check-circle': 'check-circ',
  alert: 'alert',
  clock: 'clock',
  voicemail: 'chat',
  shield: 'shield',
  'shield-check': 'shield-check',
};

/** KPI sentiment → accent colour. */
export const KPI_TONE_COLOR: Record<KpiTone, string> = {
  positive: CL_TOKENS.green,
  negative: CL_TOKENS.negative,
  warning: CL_TOKENS.warning,
  neutral: CL_TOKENS.ink3,
};

/* ---- Top-level META ----------------------------------------------------- */

export const RECEPTIONIST_CALL_LOGS_META = {
  tabs: CALL_LOGS_TABS,
  columns: CALL_LOGS_COLUMNS,
  filterChips: FILTER_CHIPS_BY_TAB,
  intent: INTENT_META,
  outcome: OUTCOME_META,
  sentiment: SENTIMENT_META,
  recallType: RECALL_TYPE_META,
  outboundOutcome: OUTBOUND_OUTCOME_META,
  status: STATUS_META,
  kpiIcon: KPI_ICON_KIND,
} as const;
